import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import { Injectable } from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import { environment } from "src/environments/environment";
import { CompanyDocumentType } from "../enums/company-document-type.enum";
import { CompanyVM } from "../models/company/company-vm.model";
import { CompanyDTO } from "../models/company/company-dto.model";
import { CompanyTaxInfoDTO } from "../models/company/company-tax-info-dto.model";
import { CompanyContactInfoDTO } from "../models/company/company-contact-info-dto.model";
import { CompanyBrandingInfoDTO } from "../models/company/company-branding-info-dto.model";
import { CompanyUsersResponse } from "../models/company/company-users-response.model";
import { UserDTO } from "../models/user/user-dto.model";
import { OnboardingPermissions } from "../enums/permissions/onboarding-permissions.enum";
import {CompanyUserDTO} from '../models/company/company-user-dto.model';
import {IDepartment} from '../models/departments&services/IDepartment.interface';
import {IService} from '../models/departments&services/IService.interface';
import {OrganizationsService} from '../organization-settings/organizations.service';
import { Employee } from "../company-management/employee-dashboard/models/employee-dashboard.models";
import {IOrganizationService} from '../corporate/models/IFormService.interface';
import {IOrganizationServices} from '../organization-settings/models/organization-services.model';

export type EnsureEmployeeFormStatus = 'Ok' | 'CompanyHasNoSubmittedForm' | 'CorpCreateFailed';

export interface EnsureEmployeeFormResult {
  employeeSubmittedFormId: number | null;
  status: EnsureEmployeeFormStatus;
  message?: string | null;
}

@Injectable({
    providedIn: 'root'
})

export class CompaniesService {

  private currentCompanyId$ = new BehaviorSubject<number>(undefined as number);

    url = environment.apiConfig.IdentityAPI.endpoint + '/companies';
    urlCorporate = environment.apiConfig.corporateAPI.endpoint + '/company';
    urlLegacy = environment.apiConfig.legacyInvoicingApi.endpoint + '/api/configuration';
    urlCorporateNoCompany = environment.apiConfig.corporateAPI.endpoint ;
    urlIdentity = environment.apiConfig.IdentityAPI.endpoint;
    private headers = new HttpHeaders({
        'permission' : OnboardingPermissions.OnboardingAdmin,
    });


    constructor(
      private httpClient: HttpClient,
      private organizationService: OrganizationsService,
    ) {}

   // Prefer the in-memory subject value over localStorage so a tab can override its
   // active company without poisoning the shared localStorage that other tabs are
   // using. Used by entry points that open another company in a new tab (e.g. the
   // header's "organization settings" action calls setCompanyIdObs in the new tab,
   // and this getter picks it up while leaving localStorage untouched).
   get managedCompanyId() {
     const fromObs = this.currentCompanyId$.value;
     if (fromObs) return fromObs;
     return +localStorage.getItem('managedCompanyId') as number;
   }
   checkOrganizationCompanies(): Observable<any> {
    return this.httpClient.get<any>(this.urlIdentity + '/organizations/companies/' )
}

    
  setManagedCompanyId(id: number) {
    this.currentCompanyId$.next(id);
      localStorage.setItem('managedCompanyId', JSON.stringify(id));
  }

   getManagedCompanyIdObsValue(): Observable<number> {
      return this.currentCompanyId$.asObservable();
   }

   setCompanyIdObs(id: number) {
      return this.currentCompanyId$.next(id);
   }

    getCompany(companyId: number): Observable<CompanyVM> {
        return this.httpClient.get<CompanyVM>(this.url + '/' + companyId, { headers: this.headers });
    }

    createCompany(company: CompanyDTO): Observable<number> {
      if (this.organizationService.isFirstCompany()) {
        return  this.httpClient.post<number>(this.url, company);
      }
      return this.httpClient.post<number>(this.url, company,{ headers: this.headers });

    }

    checkRegistrationNumber(registrationNumber: string): Observable<number> {
        return this.httpClient.get<number>(this.url + '/checkRegistrationNumber/' + registrationNumber)
    }

    updateCompanyGeneralInfo(companyId: number, companyGeneralInfoDTO: CompanyDTO): Observable<void> {
      if (this.organizationService.isFirstCompany()) {
        return this.httpClient.put<void>(this.url + `/${companyId}/general`, companyGeneralInfoDTO);
      }
        return this.httpClient.put<void>(this.url + `/${companyId}/general`, companyGeneralInfoDTO, { headers: this.headers });
    }

    setCompanyDynamics(companyId: number, hasDynamics: boolean): Observable<void> {
      const body = { hasDynamics };
      if (this.organizationService.isFirstCompany()) {
        return this.httpClient.put<void>(this.url + `/${companyId}/dynamics`, body);
      }
        return this.httpClient.put<void>(this.url + `/${companyId}/dynamics`, body, { headers: this.headers });
    }

    updateCompanyTaxInfo(companyId: number, companyTaxInfo: CompanyTaxInfoDTO): Observable<void> {
      if (this.organizationService.isFirstCompany()) {
        return this.httpClient.put<void>(this.url + `/${companyId}/tax`, companyTaxInfo);
      }
        return this.httpClient.put<void>(this.url + `/${companyId}/tax`, companyTaxInfo, { headers: this.headers });
    }

    updateCompanyContactInfo(companyId: number, companyContactInfo: CompanyContactInfoDTO): Observable<void> {
      if (this.organizationService.isFirstCompany()) {
        return this.httpClient.put<void>(this.url + `/${companyId}/contact`, companyContactInfo);
      }
        return this.httpClient.put<void>(this.url + `/${companyId}/contact`, companyContactInfo, { headers: this.headers });
    }

    updateCompanyBrandingInfo(companyId: number, companyBrandingInfo: CompanyBrandingInfoDTO): Observable<void> {
      if (this.organizationService.isFirstCompany()) {
        return this.httpClient.put<void>(this.url + `/${companyId}/branding`, companyBrandingInfo);
      }
        return this.httpClient.put<void>(this.url + `/${companyId}/branding`, companyBrandingInfo, { headers: this.headers });
    }

    uploadCompanyDocument(companyId: number, documentType: CompanyDocumentType, file: File): Observable<string> {
        const formData = new FormData();
        formData.append('file', file);
        return this.httpClient.post<string>(this.url + `/${companyId}/upload?documentType=${documentType}`, formData, { headers: this.headers });
    }

    getCompanyUsers(companyId?: number): Observable<CompanyUsersResponse> {
      if (companyId) {
        return this.httpClient.get<CompanyUsersResponse>(this.url + `/${companyId}/users`, { headers: this.headers });
      }
      return  this.httpClient.get<CompanyUsersResponse>(this.url + `/${this.managedCompanyId}/users`);
    }

    getOrganizationUsers(): Observable<any> {
      return this.httpClient.get<any>(this.urlIdentity + `/organizations/users`, { headers: this.headers });
    }


    getCompanyUser(employeeId?: number): Observable<any> {
        return this.httpClient.get<any>(this.url + `/${this.managedCompanyId}/users/${employeeId}`, { headers: this.headers });
    }
    private currentEmployeeSubject = new BehaviorSubject<any | null>(null);
    setCurrentEmployee(employee: any) {
      this.currentEmployeeSubject.next(employee);
    }
  
    getCurrentEmployee(): Observable<any | null> {
      return this.currentEmployeeSubject.asObservable();
    }

   


    inviteCompanyUser(companyId: number, userDTO: UserDTO): Observable<void> {
        return this.httpClient.post<void>(this.url + `/${companyId}/invite`, userDTO, { headers: this.headers });
    }

    requestJoinCompany(companyId: number) : Observable<void> {
        return this.httpClient.post<void>(this.url + `/${companyId}/joinRequest`, {});
    }

    approveUserJoinCompanyRequest(companyId: number, azureObjectId: string): Observable<void> {
        return this.httpClient.post<void>(this.url + `/${companyId}/approveUser?azureObjectId=${azureObjectId}`, { headers: this.headers });
    }

    rejectUserJoinCompanyRequest(companyId: number, azureObjectId: string): Observable<void> {
    return this.httpClient.post<void>(this.url + `/${companyId}/users/reject?azureObjectId=${azureObjectId}`, { headers: this.headers });
    }

    getModuleUsers(companyId: number, module: number): Observable<CompanyUserDTO[]> {
      return this.httpClient.get<CompanyUserDTO[]>(this.url +`/${companyId}/modules/${module}/users`);
    }

    changeUserModulePermission(companyId: number, module: number, userId: number, permission: number) {
      return this.httpClient.post(this.url + `/${companyId}/modules/${module}/users/${userId}/permissions/${permission}`, {})
    }

    removeUserModulePermission(companyId: number, module: number, userId: number): Observable<void> {
      return this.httpClient.delete<void>(this.url + `/${companyId}/modules/${module}/users/${userId}`)
    }

    addUserToCompany(companyId: number, userId: number): Observable<void> {
      return this.httpClient.post<void>(this.url + `/${companyId}/users/${userId}`, {})
    }

    removeUserFromCompany(companyId: number, userId: number): Observable<void> {
      return this.httpClient.delete<void>(this.url + `/${companyId}/users/${userId}`)
    }

    // Lazy / backfill: ensures a SubmittedForm (BusinessStatus = Employee) exists for this
    // company-user in BC_Corporate so payroll integration can attach.
    // Status === 'Ok' when a form is set; otherwise message explains what to fix.
    ensureEmployeeSubmittedForm(userId: number, companyId?: number): Observable<EnsureEmployeeFormResult> {
      const cid = companyId ?? this.managedCompanyId;
      return this.httpClient.post<EnsureEmployeeFormResult>(
        this.url + `/${cid}/users/${userId}/ensure-employee-form`,
        {}
      );
    }

    // Manual link: assigns an existing SubmittedFormId directly to the company-user.
    setEmployeeSubmittedForm(userId: number, submittedFormId: number, companyId?: number): Observable<{ employeeSubmittedFormId: number }> {
      const cid = companyId ?? this.managedCompanyId;
      return this.httpClient.put<{ employeeSubmittedFormId: number }>(
        this.url + `/${cid}/users/${userId}/employee-form/${submittedFormId}`,
        {}
      );
    }

    // Departments & Services

  //Departments

  getDepartments(includeServices: boolean, companyId?: number): Observable<IDepartment[]> {
    const params = new HttpParams()
      .set('includeServices', includeServices);
    const id = companyId ? companyId : this.managedCompanyId;
    return this.httpClient.get<IDepartment[]>(this.urlCorporate + `/${id}/departments`, {
      params: params
    });
  }

  getAllDepartments(includeServices: boolean = false): Observable<IDepartment[]> {
    const params = new HttpParams()
      .set('includeServices', includeServices);
    return this.httpClient.get<IDepartment[]>(this.urlCorporateNoCompany + `/organization/departments`, {
      params: params
    });
  }

  /**
   * Get all services from all organizations
   * GET /api/organization/services
   */
  getOrganizationServices(): Observable<IOrganizationService[]> {
    return this.httpClient.get<IOrganizationService[]>(this.urlCorporateNoCompany + `/api/organization/services`);
  }

  /**
   * Get organization service templates grouped by departments
   * GET /api/organization/services (returns array of companies with departments and service templates)
   */
  getOrganizationServiceTemplates(): Observable<IOrganizationServices[]> {
    return this.httpClient.get<IOrganizationServices[]>(this.urlCorporateNoCompany + `/api/organization/services`);
  }
  addDepartment(companyId: number, department: {name: string}): Observable<number> {
    return this.httpClient.post<number>(this.urlCorporate + `/${companyId}/departments`, department);
  }

  addExistingDepartment(companyId: number, id: number): Observable<number> {
    return this.httpClient.post<number>(this.urlCorporate + `/${companyId}/departments/${id}`, {});
  }

  updateDepartment(companyId: number, department: {name: string, account:string}, departmentId: number) {
    return this.httpClient.put(this.urlCorporate + `/${companyId}/departments/` + departmentId, department);
  }

  deleteDepartment(companyId: number, id: number) {
    return this.httpClient.delete(this.urlCorporate + `/${companyId}/departments/` + id);
  }

  // Services

  getServices(departmentId: number) {
     return this.httpClient.get(this.urlCorporate + `/${this.managedCompanyId}/departments/${departmentId}/services`);
  }

  addService(companyId: number, service: IService, departmentId: number): Observable<number> {
    return this.httpClient.post<number>(this.urlCorporate + `/${companyId}/departments/${departmentId}/services`, service);
  }

  updateService(companyId: number, service: IService, departmentId: number) {
    return this.httpClient.put(this.urlCorporate + `/${companyId}/departments/${departmentId}/services/${service.id}`, service);
  }

  deleteService(companyId: number, serviceId: number, departmentId: number) {
    return this.httpClient.delete(this.urlCorporate + `/${companyId}/departments/${departmentId}/services/${serviceId}`);
  }

  // Database Name (Legacy API)
  getDatabaseName(companyId: number): Observable<{ companyId: number; databaseName: string }> {
    return this.httpClient.get<{ companyId: number; databaseName: string }>(this.urlLegacy + `/CompanyDatabaseMappings/${companyId}/database-name`);
  }

  updateDatabaseName(companyId: number, databaseName: string): Observable<void> {
    return this.httpClient.put<void>(this.urlLegacy + `/CompanyDatabaseMappings/${companyId}`, { databaseName });
  }
}
